import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  DocumentIcon,
  FolderIcon,
  CheckIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { filesApi } from '../../api/files';
import { foldersApi } from '../../api/folders';
import { portalsApi } from '../../api/clients';
import { useToast } from '../ui/Toaster';
import type { FileItem, FolderItem } from '../../stores/fileStore';
import clsx from 'clsx';

interface PortalFileSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  currentFileIds: string[];
  onFilesAdded: (files: any[]) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function PortalFileSelector({
  isOpen,
  onClose,
  clientId,
  currentFileIds,
  onFilesAdded,
}: PortalFileSelectorProps) {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string; name: string }[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadFiles(null);
      setSelectedFileIds(new Set());
    }
  }, [isOpen]);

  const loadFiles = async (folderId: string | null) => {
    setIsLoading(true);
    try {
      const [filesRes, foldersRes] = await Promise.all([
        filesApi.list({ folderId, trashed: false }),
        foldersApi.list(folderId),
      ]);
      setFiles(filesRes.files);
      setFolders(foldersRes.folders);
      setCurrentFolderId(folderId);

      // Update breadcrumb
      if (folderId) {
        const breadcrumbRes = await foldersApi.getBreadcrumb(folderId);
        setBreadcrumb(breadcrumbRes.breadcrumb);
      } else {
        setBreadcrumb([]);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleAddFiles = async () => {
    if (selectedFileIds.size === 0) return;

    setIsAdding(true);
    try {
      const response = await portalsApi.addFiles(clientId, Array.from(selectedFileIds));
      toast.success(`Added ${response.added} file${response.added !== 1 ? 's' : ''} to portal`);
      onFilesAdded(response.files);
    } catch (error: any) {
      toast.error(error.message || 'Failed to add files');
    } finally {
      setIsAdding(false);
    }
  };

  const navigateToFolder = (folderId: string | null) => {
    loadFiles(folderId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Files to Portal"
      description="Select files to share with your client"
      size="lg"
    >
      <div className="space-y-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigateToFolder(null)}
            className={clsx(
              'hover:text-primary-600 dark:hover:text-primary-400 transition-colors',
              currentFolderId === null
                ? 'text-primary-600 dark:text-primary-400 font-medium'
                : 'text-gray-500 dark:text-slate-400'
            )}
          >
            My Files
          </button>
          {breadcrumb.map((item, index) => (
            <div key={item.id} className="flex items-center gap-2">
              <span className="text-gray-300 dark:text-slate-600">/</span>
              <button
                onClick={() => navigateToFolder(item.id)}
                className={clsx(
                  'hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate max-w-[120px]',
                  index === breadcrumb.length - 1
                    ? 'text-primary-600 dark:text-primary-400 font-medium'
                    : 'text-gray-500 dark:text-slate-400'
                )}
              >
                {item.name}
              </button>
            </div>
          ))}
        </div>

        {/* File List */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : files.length === 0 && folders.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-slate-400">
              No files in this folder
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {/* Back button for subfolders */}
              {currentFolderId && (
                <button
                  onClick={() => {
                    const parentId =
                      breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].id : null;
                    navigateToFolder(parentId);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-b border-gray-100 dark:border-slate-700"
                >
                  <ArrowLeftIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600 dark:text-slate-300">Back</span>
                </button>
              )}

              {/* Folders */}
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => navigateToFolder(folder.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                >
                  <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                    <FolderIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white flex-1 text-left truncate">
                    {folder.name}
                  </span>
                </button>
              ))}

              {/* Files */}
              {files.map((file) => {
                const isAlreadyInPortal = currentFileIds.includes(file.id);
                const isSelected = selectedFileIds.has(file.id);

                return (
                  <motion.button
                    key={file.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => !isAlreadyInPortal && toggleFileSelection(file.id)}
                    disabled={isAlreadyInPortal}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-3 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0',
                      isAlreadyInPortal
                        ? 'bg-gray-50 dark:bg-slate-800 opacity-50 cursor-not-allowed'
                        : isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                    )}
                  >
                    <div
                      className={clsx(
                        'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                        isAlreadyInPortal
                          ? 'border-gray-300 dark:border-slate-600 bg-gray-200 dark:bg-slate-700'
                          : isSelected
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300 dark:border-slate-600'
                      )}
                    >
                      {(isSelected || isAlreadyInPortal) && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                      <DocumentIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {formatFileSize(file.size)}
                        {isAlreadyInPortal && ' • Already in portal'}
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {selectedFileIds.size} file{selectedFileIds.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAddFiles}
              disabled={selectedFileIds.size === 0}
              isLoading={isAdding}
            >
              Add to Portal
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
